import { GroupElement, type VisualElement } from '@bynder-studio/render-core';

type ElementWithParent = Pick<VisualElement, 'id' | 'parent'>;

/**
 * When children of a DES (Single Select) group are being wrapped into a new sub-group,
 * returns the DES parent groups whose activeDropdownElementId is among the wrapped elements.
 * After wrapping, the caller should update activeDropdownElementId to point to the new group.
 */
export const collectDesParentsForGrouping = (elements: ElementWithParent[]): GroupElement[] => {
    const elementIds = new Set(elements.map((el) => el.id));
    const seen = new Set<number>();

    return elements.reduce<GroupElement[]>((result, el) => {
        const { parent } = el;

        if (
            parent instanceof GroupElement &&
            parent.onlyAllowOneSelection &&
            elementIds.has(parent.activeDropdownElementId) &&
            !seen.has(parent.id)
        ) {
            seen.add(parent.id);
            result.push(parent);
        }

        return result;
    }, []);
};

/**
 * When a sub-group inside a DES group is being unwrapped, returns the update needed:
 * the DES parent id and the new active child id (highest render-order child of the unwrapped group).
 * Returns null if the group is not the active selection of a DES parent.
 */
export const getDesUpdateForUngroup = (
    group: Pick<GroupElement, 'id' | 'parent' | 'children'>,
): { desGroupId: number; newActiveChildId: number } | null => {
    const { parent: desParent } = group;

    if (
        !(desParent instanceof GroupElement) ||
        !desParent.onlyAllowOneSelection ||
        desParent.activeDropdownElementId !== group.id
    ) {
        return null;
    }

    const firstChild = [...group.children].sort((a, b) => b.renderOrder - a.renderOrder)[0];

    if (!firstChild) {
        return null;
    }

    return { desGroupId: desParent.id, newActiveChildId: firstChild.id };
};

/**
 * When an element is being moved out of a DES group (reparent / add-to-different-group),
 * returns the update needed if the element was the active dropdown selection.
 * The next highest render-order sibling becomes the new active child.
 *
 * Note: nested empty-group cascades (DES > group > group > element) are handled
 * reactively by removeEmptyGroupElements in render-core.
 *
 * Returns null if no DES update is needed.
 */
export const getDesUpdateForChildRemoval = (
    element: ElementWithParent,
): { desGroupId: number; newActiveChildId: number } | null => {
    const { parent } = element;

    if (
        !(parent instanceof GroupElement) ||
        !parent.onlyAllowOneSelection ||
        parent.activeDropdownElementId !== element.id
    ) {
        return null;
    }

    const nextChild = [...parent.children]
        .filter((child) => child.id !== element.id)
        .sort((a, b) => b.renderOrder - a.renderOrder)[0];

    if (!nextChild) {
        return null;
    }

    return { desGroupId: parent.id, newActiveChildId: nextChild.id };
};
